import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { AgentSummary } from '@/hooks/useAIAgents';
import { useAxios } from '@/hooks/useAxios';
import { Asset, Risk, Webpage } from '@/types';

interface Capability {
  name: string;
  title?: string;
  description?: string;
  target?: string;
}

interface Command {
  command: string;
  description: string;
  prefix?: string;
  data?: Asset | Risk | Webpage | Capability | AgentSummary; // Full object data for entity search
}

const COMMANDS: Command[] = [
  {
    command: '/mode',
    description: 'Toggle between Query and Agent mode',
    prefix: '/',
  },
];

export const useCommandMenu = (
  newMessage: string,
  isPraetorianUser: boolean,
  onModeChange: (mode: 'query' | 'agent') => void,
  onClearInput: () => void,
  onSearch?: (
    type: 'asset' | 'risk' | 'webpage' | 'capability',
    query: string
  ) => void,
  agents?: AgentSummary[],
  mode?: 'query' | 'agent'
) => {
  const axios = useAxios();
  const [recentAssets, setRecentAssets] = useState<Asset[]>([]);
  const [recentRisks, setRecentRisks] = useState<Risk[]>([]);
  const [recentWebpages, setRecentWebpages] = useState<Webpage[]>([]);
  const [recentCapabilities, setRecentCapabilities] = useState<Capability[]>(
    []
  );

  // Detect current word being typed for autocomplete
  const getCurrentWord = () => {
    const cursorPos = newMessage.length; // Assuming cursor is at end
    const textBeforeCursor = newMessage.slice(0, cursorPos);
    const words = textBeforeCursor.split(/\s+/);
    return words[words.length - 1] || '';
  };

  const currentWord = getCurrentWord();
  const [debouncedCurrentWord] = useDebounce(currentWord, 300);

  // Derive search staleness - when currentWord changes, hide old results until debounce settles
  const isSearchStale = currentWord !== debouncedCurrentWord;

  // Menu state calculated directly during render (React Compiler handles memoization)
  const { showCommandMenu, menuType } = (() => {
    if (currentWord.startsWith('@') && !currentWord.startsWith('$')) {
      return { showCommandMenu: true, menuType: 'agent' as const };
    }
    if (currentWord.startsWith('/agent:') && isPraetorianUser) {
      return { showCommandMenu: true, menuType: 'agent' as const };
    }
    if (currentWord.startsWith('/')) {
      const isTypingModeSwitch =
        isPraetorianUser &&
        currentWord === newMessage &&
        '/mode'.startsWith(newMessage) &&
        newMessage.startsWith('/m');
      if (isTypingModeSwitch) {
        return { showCommandMenu: true, menuType: 'command' as const };
      }
      return { showCommandMenu: true, menuType: 'search' as const };
    }
    if (
      currentWord.startsWith('$') ||
      currentWord.startsWith('#') ||
      currentWord.startsWith('&')
    ) {
      return { showCommandMenu: true, menuType: 'search' as const };
    }
    return { showCommandMenu: false, menuType: 'command' as const };
  })();

  // Filter agents directly during render (React Compiler handles memoization)
  const recentAgents = (() => {
    const isAgentMention =
      debouncedCurrentWord.startsWith('@') &&
      !debouncedCurrentWord.startsWith('$');
    const isAgentSlash = debouncedCurrentWord.startsWith('/agent:');

    if (!isAgentMention && !isAgentSlash) {
      return [];
    }

    const searchTerm = isAgentSlash
      ? debouncedCurrentWord.slice(7).trim() // Remove '/agent:' prefix
      : debouncedCurrentWord.slice(1).trim(); // Remove '@' prefix

    if (!agents) {
      return [];
    }

    return agents.filter(
      agent =>
        agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agent.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  })();

  // Visible entity results - hide stale results during debounce
  const visibleAssets = isSearchStale ? [] : recentAssets;
  const visibleRisks = isSearchStale ? [] : recentRisks;
  const visibleWebpages = isSearchStale ? [] : recentWebpages;
  const visibleCapabilities = isSearchStale ? [] : recentCapabilities;
  const visibleAgents = isSearchStale ? [] : recentAgents;

  // Fetch assets when searching (debounced to avoid too many requests)
  useEffect(() => {
    if (debouncedCurrentWord.startsWith('$')) {
      const searchTerm = debouncedCurrentWord.slice(1).trim();

      const filters: any[] = [];

      // Add status filter first
      filters.push({ field: 'status', operator: '=', value: 'A' });

      // Search only on dns field since that's what we display
      if (searchTerm) {
        filters.push({
          field: 'dns',
          operator: 'CONTAINS',
          value: searchTerm,
        });
      }

      axios
        .post('/my', {
          node: { labels: ['Asset'], filters, relationships: [] },
          limit: 50,
          orderBy: 'dns',
          descending: false,
        })
        .then(response => {
          setRecentAssets(response.data?.assets || []);
        })
        .catch(err => console.error('Failed to fetch assets:', err));
    }
  }, [debouncedCurrentWord, axios]);

  // Fetch risks when searching (debounced to avoid too many requests)
  useEffect(() => {
    if (debouncedCurrentWord.startsWith('#')) {
      const searchTerm = debouncedCurrentWord.slice(1).trim();

      const filters: any[] = [];

      // Search only on name field since that's what we display
      if (searchTerm) {
        filters.push({
          field: 'name',
          operator: 'CONTAINS',
          value: searchTerm,
        });
      }

      axios
        .post('/my', {
          node: { labels: ['Risk'], filters, relationships: [] },
          limit: 50,
          orderBy: 'name',
          descending: false,
        })
        .then(response => {
          setRecentRisks(response.data?.risks || []);
        })
        .catch(err => console.error('Failed to fetch risks:', err));
    }
  }, [debouncedCurrentWord, axios]);

  // Fetch webpages when searching (debounced to avoid too many requests)
  useEffect(() => {
    if (debouncedCurrentWord.startsWith('&')) {
      const searchTerm = debouncedCurrentWord.slice(1).trim();

      const filters: any[] = [];

      // Add status filter first
      filters.push({ field: 'status', operator: '=', value: 'A' });

      // Search on url field since that's what we display
      if (searchTerm) {
        filters.push({
          field: 'url',
          operator: 'CONTAINS',
          value: searchTerm,
        });
      }

      axios
        .post('/my', {
          node: { labels: ['Webpage'], filters, relationships: [] },
          limit: 50,
          orderBy: 'url',
          descending: false,
        })
        .then(response => {
          setRecentWebpages(response.data?.webpages || []);
        })
        .catch(err => console.error('Failed to fetch webpages:', err));
    }
  }, [debouncedCurrentWord, axios]);

  // Fetch capabilities when searching (debounced to avoid too many requests)
  useEffect(() => {
    const isModeSwitch = debouncedCurrentWord.startsWith('/mode');

    if (debouncedCurrentWord.startsWith('/') && !isModeSwitch) {
      const searchTerm = debouncedCurrentWord.slice(1).trim();

      // Use the capabilities API endpoint with query parameters
      const params = new URLSearchParams();
      if (searchTerm) {
        params.set('name', searchTerm);
      }

      axios
        .get(`/capabilities?${params.toString()}`)
        .then(response => {
          // API returns array directly, not wrapped in object
          const capabilities = Array.isArray(response.data)
            ? response.data
            : [];
          setRecentCapabilities(capabilities);
        })
        .catch(err => console.error('Failed to fetch capabilities:', err));
    }
  }, [debouncedCurrentWord, axios]);

  // Get filtered items based on input and menu type
  const getFilteredCommands = (): Command[] => {
    if (menuType === 'agent') {
      const isAtMention =
        currentWord.startsWith('@') && !currentWord.startsWith('$');
      return visibleAgents.map(agent => ({
        command: isAtMention ? `@${agent.name}` : `/agent:${agent.name}`,
        description: `${agent.title} - ${agent.description}`,
        prefix: isAtMention ? '@' : '/agent:',
        data: agent,
      }));
    }

    if (menuType === 'search') {
      // Show assets - already filtered by server, no slicing needed
      if (currentWord.startsWith('$')) {
        return visibleAssets.map(asset => ({
          command: `$${asset.dns || asset.name}`,
          description: asset.name || asset.identifier || 'Asset',
          data: asset,
        }));
      }
      // Show risks - already filtered by server, no slicing needed
      if (currentWord.startsWith('#')) {
        return visibleRisks.map((risk, index) => ({
          command: `#${risk.name}`,
          description: risk.dns || risk.key || `Risk ${index + 1}`,
          data: risk,
        }));
      }
      // Show webpages - already filtered by server, no slicing needed
      if (currentWord.startsWith('&')) {
        return visibleWebpages.map(webpage => ({
          command: `&${webpage.url}`,
          description: webpage.source?.join(', ') || webpage.key || 'Webpage',
          data: webpage,
        }));
      }
      // Show capabilities - already filtered by server, no slicing needed
      if (currentWord.startsWith('/') && !currentWord.startsWith('/mode')) {
        return visibleCapabilities.map(capability => ({
          command: `/${capability.name}`,
          description:
            capability.title ||
            capability.description ||
            `${capability.target || 'Capability'}`,
          data: capability,
        }));
      }
      return [];
    }

    // For commands
    if (!newMessage || newMessage === '/') {
      return COMMANDS;
    }
    return COMMANDS.filter(cmd =>
      cmd.command.startsWith(newMessage.toLowerCase())
    );
  };

  const filteredCommands = getFilteredCommands();

  // Check if message is a command and execute it
  const executeCommandIfMatch = (message: string): boolean => {
    // Mode switch commands (Praetorian only)
    if (isPraetorianUser) {
      if (message === '/mode') {
        onModeChange(mode === 'query' ? 'agent' : 'query');
        onClearInput();
        return true;
      }
      if (message === '/mode query') {
        onModeChange('query');
        onClearInput();
        return true;
      }
      if (message === '/mode agent') {
        onModeChange('agent');
        onClearInput();
        return true;
      }
    }

    // Entity search (all users)
    if (onSearch) {
      if (message.startsWith('$')) {
        const query = message.slice(1).trim();
        onSearch('asset', query);
        onClearInput();
        return true;
      }
      if (message.startsWith('#')) {
        const query = message.slice(1).trim();
        onSearch('risk', query);
        onClearInput();
        return true;
      }
      if (message.startsWith('&')) {
        const query = message.slice(1).trim();
        onSearch('webpage', query);
        onClearInput();
        return true;
      }
      // Capability search - only if not a mode switch command
      if (message.startsWith('/') && !message.startsWith('/mode')) {
        const query = message.slice(1).trim();
        onSearch('capability', query);
        onClearInput();
        return true;
      }
    }

    return false;
  };

  // Auto-complete if there's exactly one matching command
  const shouldAutoComplete =
    showCommandMenu && filteredCommands.length === 1 && menuType === 'command';

  const selectCommand = (command: string) => {
    // Commands don't need special handling - they execute immediately
    executeCommandIfMatch(command);
    // Menu will hide automatically when onClearInput() resets newMessage, making currentWord empty
  };

  const replaceCurrentWord = (
    replacement: string,
    data?: Asset | Risk | Webpage | Capability
  ): string => {
    const cursorPos = newMessage.length;
    const textBeforeCursor = newMessage.slice(0, cursorPos);
    const textAfterCursor = newMessage.slice(cursorPos);
    const words = textBeforeCursor.split(/\s+/);

    // If we have entity data, use @{key} format for precise backend lookup
    if (data && 'key' in data) {
      const entityRef = `@{${data.key}}`;
      words.pop();
      return (
        words.join(' ') +
        (words.length > 0 ? ' ' : '') +
        entityRef +
        textAfterCursor
      );
    }

    // For capabilities, just use the name
    if (data && 'name' in data && !('key' in data)) {
      words.pop();
      return (
        words.join(' ') +
        (words.length > 0 ? ' ' : '') +
        replacement +
        textAfterCursor
      );
    }

    // Replace the last word with the selected item
    words[words.length - 1] = replacement;
    return words.join(' ') + ' ' + textAfterCursor;
  };

  return {
    showCommandMenu,
    filteredCommands,
    executeCommandIfMatch,
    shouldAutoComplete,
    selectCommand,
    replaceCurrentWord,
    menuType,
  };
};
